/**
 * @author Rejo Varghese
 * @description PDF for an operator settlement.
 *              Renders header + line items + totals using @react-pdf/renderer.
 */

import React from "react";
import { Document, Page, View, Text, Image, Font, StyleSheet, PDFDownloadLink, BlobProvider } from "@react-pdf/renderer";
import { Button, Space, message } from "antd";
import robotoRegular from "./fonts/Roboto-Regular.ttf";
import robotoMedium from "./fonts/Roboto-Medium.ttf";

const LOGO_URL = "https://storage.googleapis.com/customer-website-data/DAFIDY/logo.png";

Font.register({
    family: "Roboto",
    fonts: [
        { src: robotoRegular, fontWeight: 400 },
        { src: robotoMedium, fontWeight: 700 }
    ]
});

const styles = StyleSheet.create({
    page: {
        padding: 24,
        fontSize: 9,
        backgroundColor: "#FFFFFF",
        fontFamily: "Roboto"
    },
    headerRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 12,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: "#222"
    },
    brandRow: { flexDirection: "row", alignItems: "center" },
    logo: { width: 36, height: 36, marginRight: 8, objectFit: "contain" },
    brand: { fontSize: 18, fontWeight: 700, color: "#222" },
    metaCol: { textAlign: "right" },
    metaLabel: { fontSize: 7, color: "#888" },
    metaVal: { fontSize: 9, color: "#222", marginBottom: 2 },
    invoiceTitle: { fontSize: 12, fontWeight: 700, marginTop: 8, marginBottom: 8 },
    twoCol: { flexDirection: "row", marginBottom: 12 },
    block: { flex: 1, paddingRight: 12 },
    blockHeading: { fontSize: 8, color: "#888", marginBottom: 2 },
    blockValue: { fontSize: 10, fontWeight: 700, marginBottom: 4 },
    operatorName: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
    table: { borderTopWidth: 1, borderTopColor: "#222", marginTop: 8 },
    tableRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: "#bbb", paddingVertical: 4 },
    tableHeader: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#222", paddingVertical: 5 },
    th: { fontSize: 8, fontWeight: 700, color: "#222" },
    td: { fontSize: 8, color: "#222" },
    colDescription: { flex: 4, paddingRight: 4 },
    colQty: { flex: 1, textAlign: "right", paddingRight: 4 },
    colUnit: { flex: 1, textAlign: "right", paddingRight: 4 },
    colCharge: { flex: 1.2, textAlign: "right", paddingRight: 4 },
    colCredit: { flex: 1.2, textAlign: "right" },
    totalsBlock: {
        marginTop: 16,
        flexDirection: "row",
        justifyContent: "flex-end"
    },
    totalsTable: { width: 260 },
    totalsRow: { flexDirection: "row", justifyContent: "space-between", paddingVertical: 2 },
    totalsLabel: { fontSize: 9, color: "#444" },
    totalsValue: { fontSize: 9, color: "#222" },
    totalsSeparator: { borderTopWidth: 1, borderTopColor: "#222", marginTop: 4, marginBottom: 4 },
    totalsHighlightRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        backgroundColor: "#E8F5E9",
        padding: 6,
        marginTop: 4,
        borderRadius: 2
    },
    totalsHighlightLabel: { fontSize: 13, fontWeight: 700 },
    totalsHighlightValue: { fontSize: 13, fontWeight: 700 },
    footnote: {
        marginTop: 24,
        paddingTop: 8,
        borderTopWidth: 0.5,
        borderTopColor: "#bbb",
        fontSize: 7,
        color: "#888"
    }
});

// Plain Indian-grouped 2-decimal number (e.g. 12,34,567.89) — for quantities.
const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Money: ₹ + Indian grouping, sign kept before the symbol (e.g. -₹1,234.00).
const inr = (n) => {
    const num = Number(n || 0);
    return (num < 0 ? "-₹" : "₹") + fmtNum(Math.abs(num));
};
const fmtDate = (d) => d ? String(d).slice(0, 10) : "—";

const SettlementInvoiceDocument = ({ header, lines }) => {
    if (!header) return null;
    const activeLines = (lines || []).filter((l) => l.is_active !== 0);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={styles.headerRow}>
                    <View style={styles.brandRow}>
                        <Image style={styles.logo} src={LOGO_URL} />
                        <View>
                            <Text style={styles.brand}>QwikBus</Text>
                            <Text style={styles.metaVal}>Settlement</Text>
                        </View>
                    </View>
                    <View style={styles.metaCol}>
                        <Text style={styles.metaLabel}>Settlement #</Text>
                        <Text style={styles.metaVal}>{header.settlement_no}</Text>
                        <Text style={styles.metaLabel}>FY</Text>
                        <Text style={styles.metaVal}>{header.financial_year}</Text>
                        <Text style={styles.metaLabel}>Status</Text>
                        <Text style={styles.metaVal}>{header.status}</Text>
                    </View>
                </View>

                <View style={styles.twoCol}>
                    <View style={styles.block}>
                        <Text style={styles.blockHeading}>Operator</Text>
                        <Text style={styles.operatorName}>{header.operator_name || header.operator_code}</Text>
                        {header.operator_number ? (
                            <Text style={styles.td}>Operator #{header.operator_number}</Text>
                        ) : null}
                        <Text style={styles.td}>Code: {header.operator_code}</Text>
                    </View>
                    <View style={styles.block}>
                        <Text style={styles.blockHeading}>Billing Period</Text>
                        <Text style={styles.blockValue}>
                            {fmtDate(header.period_start)}  -  {fmtDate(header.period_end)}
                        </Text>
                        <Text style={styles.td}>Cycle: {header.cycle}</Text>
                    </View>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.th, styles.colDescription]}>Description</Text>
                        <Text style={[styles.th, styles.colQty]}>Qty</Text>
                        <Text style={[styles.th, styles.colUnit]}>Unit</Text>
                        <Text style={[styles.th, styles.colCharge]}>Charge</Text>
                        <Text style={[styles.th, styles.colCredit]}>Credit</Text>
                    </View>
                    {activeLines.map((l) => (
                        <View key={l.id} style={styles.tableRow}>
                            <Text style={[styles.td, styles.colDescription]}>{l.description}</Text>
                            <Text style={[styles.td, styles.colQty]}>{l.quantity == null ? "—" : fmtNum(l.quantity)}</Text>
                            <Text style={[styles.td, styles.colUnit]}>{l.unit_price == null ? "—" : l.unit_price}</Text>
                            <Text style={[styles.td, styles.colCharge]}>{inr(l.charge_amount)}</Text>
                            <Text style={[styles.td, styles.colCredit]}>{inr(l.credit_amount)}</Text>
                        </View>
                    ))}
                </View>

                <View style={styles.totalsBlock}>
                    <View style={styles.totalsTable}>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>Sub Total (Credits)</Text>
                            <Text style={styles.totalsValue}>{inr(header.sub_total_credits)}</Text>
                        </View>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>Sub Total (Charges)</Text>
                            <Text style={styles.totalsValue}>{inr(header.sub_total_charges)}</Text>
                        </View>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>GST (18%)</Text>
                            <Text style={styles.totalsValue}>{inr(header.gst_amount)}</Text>
                        </View>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>Invoice Amount</Text>
                            <Text style={styles.totalsValue}>{inr(header.invoice_amount)}</Text>
                        </View>
                        <View style={styles.totalsSeparator} />
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>Opening Balance</Text>
                            <Text style={styles.totalsValue}>{inr(header.opening_balance)}</Text>
                        </View>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>Amount Paid</Text>
                            <Text style={styles.totalsValue}>{inr(header.amount_paid)}</Text>
                        </View>
                        <View style={styles.totalsRow}>
                            <Text style={styles.totalsLabel}>Outstanding</Text>
                            <Text style={styles.totalsValue}>{inr(header.amount_outstanding)}</Text>
                        </View>
                        <View style={styles.totalsHighlightRow}>
                            <Text style={styles.totalsHighlightLabel}>Net Settlement</Text>
                            <Text style={styles.totalsHighlightValue}>{inr(header.net_settlement)}</Text>
                        </View>
                    </View>
                </View>

                <Text style={styles.footnote}>
                    Net Settlement = Credits − Invoice Amount + Opening Balance.
                    Positive = we pay the operator; negative = operator pays us.
                    Generated electronically; no signature required.
                </Text>
            </Page>
        </Document>
    );
};

const fileNameFor = (header) => {
    const clean = (s, fallback) => String(s || fallback).replace(/[\\/:*?"<>|]+/g, "-").trim();
    const operator = clean(header.operator_name || header.operator_code, "operator");
    const settlementNo = clean(header.settlement_no, "settlement");
    return `${operator}-${settlementNo}.pdf`;
};

/**
 * Inline buttons block: View / Download / Share for a settlement.
 *
 * - View: opens the rendered PDF in a new tab.
 * - Download: anchor download via @react-pdf PDFDownloadLink.
 * - Share: uses Web Share API (mobile-friendly) with the PDF blob; falls
 *          back to the download flow on desktops where files-share is unsupported.
 */
export const SettlementInvoicePdfActions = ({ header, lines }) => {
    if (!header) return null;
    const fileName = fileNameFor(header);
    const doc = <SettlementInvoiceDocument header={header} lines={lines} />;

    const onShare = (blob) => async () => {
        try {
            const file = new File([blob], fileName, { type: "application/pdf" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: header.settlement_no,
                    text: `Settlement for ${header.operator_name || header.operator_code}`,
                    files: [file]
                });
                return;
            }
        } catch (e) { /* fall through to download */ }
        // Fallback: trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fileName;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
        message.info("Sharing not supported here — downloaded instead");
    };

    return (
        <BlobProvider document={doc}>
            {({ blob, url, loading, error }) => (
                <Space wrap>
                    <Button
                        disabled={loading || !!error || !url}
                        onClick={() => url && window.open(url, "_blank", "noopener,noreferrer")}
                    >
                        View PDF
                    </Button>
                    <PDFDownloadLink document={doc} fileName={fileName}>
                        {({ loading: dlLoading }) => (
                            <Button type="primary" disabled={dlLoading}>
                                {dlLoading ? "Preparing…" : "Download"}
                            </Button>
                        )}
                    </PDFDownloadLink>
                    <Button
                        disabled={loading || !!error || !blob}
                        onClick={blob ? onShare(blob) : undefined}
                    >
                        Share
                    </Button>
                </Space>
            )}
        </BlobProvider>
    );
};

export default SettlementInvoiceDocument;
